import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { usePartsStockAnalytics } from '@/components/features/analytics/hooks/use-parts-stock-analytics';
import { AnalyticsIntro } from '@/components/features/analytics/parts-stock/analytics-intro';
import { DeadStockSummary } from '@/components/features/analytics/parts-stock/dead-stock-summary';
import { PartsStockMatrix } from '@/components/features/analytics/parts-stock/parts-stock-matrix';
import { PartsStockSummary } from '@/components/features/analytics/parts-stock/parts-stock-summary';
import { PartsStockTable } from '@/components/features/analytics/parts-stock/parts-stock-table';

export const Route = createFileRoute('/_authenticated/analytics/parts-stock')({
  component: PartsStockPage,
});

function PartsStockPage() {
  const { t } = useTranslation();
  const { data, isLoading, isRefetching, error, refetch, deadStockSummary, deadStockInsights } =
    usePartsStockAnalytics();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('analytics.partsStock.title')}</h1>
            <p className="text-muted-foreground">{t('analytics.partsStock.description')}</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('analytics.partsStock.title')}</h1>
          <p className="text-muted-foreground">{t('analytics.partsStock.description')}</p>
        </div>
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-8 text-center">
          <p className="text-destructive">{t('analytics.partsStock.failedToLoad')}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header with intro toggle */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('analytics.partsStock.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('analytics.partsStock.description')}</p>
        </div>
        <AnalyticsIntro />
      </div>

      {/* Compact metrics strip */}
      <div className="border-b border-border/50 pb-6">
        <PartsStockSummary summary={data.summary} />
      </div>

      {/* Dead/Slow-Moving Stock Alert - only show if there's inactive stock */}
      {deadStockSummary.totalInactiveParts > 0 && (
        <DeadStockSummary
          summary={deadStockSummary}
          insights={deadStockInsights}
          onRefresh={() => refetch()}
          isRefetching={isRefetching}
        />
      )}

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Matrix - takes 2 columns */}
        <div className="lg:col-span-2">
          <PartsStockMatrix parts={data.parts} />
        </div>

        {/* Sidebar - compact table */}
        <div>
          <PartsStockTable
            parts={data.parts}
            compact={true}
            onRefresh={() => refetch()}
            isRefetching={isRefetching}
          />
        </div>
      </div>
    </div>
  );
}
